import type { ArdoqId, Maybe } from './coreTypes';
import type { SourceType } from './integrations/providersApiTypes';

export enum ProvenanceSource {
  DATA_INVENTORY = 'data-inventory',
  COMPONENT_OVERVIEW = 'component-overview',
  GRID_EDITOR = 'grid-editor',
  DATA_EXPLORER = 'data-explorer',
  SHIFTX = 'shiftx',
}

type IntegrationProvenance = {
  actorKind: 'integration';
  source: SourceType;
  sourceInstance: Maybe<{ _id: ArdoqId; entityType: 'job' }>;
};

type ResolvedUIProvenanceSource =
  | 'asset-delete'
  | 'component-copy'
  | 'component-merge'
  | 'metamodel-change'
  | 'reference-merge'
  | 'solution-upgrade'
  | 'transform-initiative-create'
  | 'workspace-copy'
  | 'workspace-delete';

type UiProvenance = {
  actorKind: 'ui';
  source: ProvenanceSource | ResolvedUIProvenanceSource | 'unknown';
  sourceInstance: null;
};

type UiBundleCopyProvenance = {
  actorKind: 'ui';
  source: 'bundle-copy';
  sourceInstance: Maybe<{ _id: ArdoqId; entityType: 'bundle' }>;
};

type UiUseCaseProvenance = {
  actorKind: 'ui';
  source: 'solution-install' | 'use-case-start';
  sourceInstance: Maybe<{ _id: ArdoqId; entityType: 'use-case' }>;
};

type UiScenarioProvenance = {
  actorKind: 'ui';
  source: 'scenario-merge' | 'transform-future-state-from-scenario';
  sourceInstance: Maybe<{ _id: ArdoqId; entityType: 'scenario' }>;
};

type UiFutureStateProvenance = {
  actorKind: 'ui';
  source:
    | 'future-state-merge'
    | 'transform-future-state-approve'
    | 'transform-future-state-copy'
    | 'transform-future-state-delete'
    | 'transform-future-state-unapprove'
    | 'transform-future-state-update';
  sourceInstance: Maybe<{ _id: ArdoqId; entityType: 'future-state' }>;
};

type UiInitiativeProvenance = {
  actorKind: 'ui';
  source:
    | 'transform-future-state-create'
    | 'transform-initiative-delete'
    | 'transform-initiative-update';
  sourceInstance: Maybe<{ _id: ArdoqId; entityType: 'initiative' }>;
};

type ApiProvenance = {
  actorKind: 'api';
  source: 'api-v1' | 'public-api';
  sourceInstance: Maybe<{ _id: ArdoqId; entityType: 'token' }>;
};

type SurveyProvenance = {
  actorKind: 'survey';
  source: 'survey-response' | 'survey-approval';
  sourceInstance: Maybe<{ _id: ArdoqId; entityType: 'survey' }>;
};

type SurveyChangeRequestProvenance = {
  actorKind: 'survey';
  source: 'change-request-approval';
  sourceInstance: Maybe<{ _id: ArdoqId; entityType: 'change-request' }>;
};

type SystemProvenance = {
  actorKind: 'system';
  source: 'migration' | 'operation';
  sourceInstance: null;
};

type SystemBundleCopyProvenance = {
  actorKind: 'system';
  source: 'bundle-copy';
  sourceInstance: Maybe<{ _id: ArdoqId; entityType: 'bundle' }>;
};

type AiAgentProvenance = {
  actorKind: 'ai-agent';
  source: 'mcp' | 'ardoq-assistant';
  sourceInstance: null;
};

export type Provenance =
  | IntegrationProvenance
  | UiProvenance
  | UiBundleCopyProvenance
  | UiUseCaseProvenance
  | UiScenarioProvenance
  | UiFutureStateProvenance
  | UiInitiativeProvenance
  | ApiProvenance
  | SurveyProvenance
  | SurveyChangeRequestProvenance
  | SystemProvenance
  | SystemBundleCopyProvenance
  | AiAgentProvenance;

export type ProvenanceFields = {
  createdProvenance: Maybe<Provenance>;
  lastProvenance: Maybe<Provenance>;
};

export const PROVENANCE_FIELD_KEYS = [
  'createdProvenance',
  'lastProvenance',
] as const satisfies ReadonlyArray<keyof ProvenanceFields>;
